import { Box, Typography, useTheme } from "@mui/material";
import { LeadSale, LeadSalesLabel } from "@/modules/lead-page/types";
import { EnvironmentVariables } from "@/modules/common/types";
import useScreenSize from "@/modules/common/hooks/device-detection.hook";
import { NwButton } from "@nawy/ui";
import { leadSalesLabels } from "@/modules/lead-page/new/constants";

interface LeadSalesProps {
  data: LeadSale;
}

export const SalesCard = ({ data }: LeadSalesProps) => {
  const theme = useTheme();
  const screen = useScreenSize();

  const getPropertyType = () => {
    return typeof data.property_type === "string" ? data.property_type : "N/A";
  };

  const getCompoundName = () => {
    return data.compound !== null ? data.compound : "N/A";
  };

  const getDeveloperName = () => {
    return data.developer !== null ? data.developer : "N/A";
  };

  const getUnitLocation = () => {
    return data.unit_location || "N/A";
  };

  const formatValue = (title: LeadSalesLabel) => {
    const value = data[title.key];
    if (title.type === "money") {
      return "EGP " + (value as number)?.toLocaleString();
    }
    if (title.type === "date" && value !== "N/A") {
      return new Date(value as string).toLocaleString("en-GB", {
        year: "numeric",
        month: "numeric",
        day: "numeric",
      });
    }
    if (typeof value === "object" && value !== null) {
      return (value as { name: string }).name;
    }
    return String(value || "N/A");
  };

  return (
    <Box
      sx={{
        backgroundColor: "#F9F9F9",
        borderRadius: theme.nawy.radius.xl,
        padding: theme.nawy.spacing.md,
        display: "flex",
        flexDirection: "column",
        gap: theme.nawy.spacing.md,
        width: "100%",
      }}
    >
      {/* Section 1: Title and Subtitle */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: theme.nawy.spacing.xxxs,
        }}
      >
        <Typography
          sx={{
            ...theme.nawy.typography.english.variants.body.lg.semibold,
            color: theme.palette.text.primary,
          }}
        >
          {`${getPropertyType()} in ${getCompoundName()} • ${data.unit_number}`}
        </Typography>
        <Typography
          sx={{
            ...theme.nawy.typography.english.variants.body.sm.regular,
            color: theme.palette.text.secondary,
          }}
        >
          {`${getDeveloperName()} • ${getUnitLocation()}`}
        </Typography>
      </Box>

      {/* Section 2: Grid of Data Fields */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: theme.nawy.spacing.md,
          rowGap: theme.nawy.spacing.md,
          columnGap: theme.nawy.spacing["2xl"],
          width: "100%",
        }}
      >
        {leadSalesLabels.map((title: LeadSalesLabel) => (
          <Box
            key={title.key}
            sx={{
              width: screen.isMobile ? "134px" : "159px",
              display: "flex",
              flexDirection: "column",
              gap: theme.nawy.spacing.xxs,
            }}
          >
            <Typography
              sx={{
                ...theme.nawy.typography.english.variants.body.sm.regular,
                color: theme.palette.text.secondary,
              }}
            >
              {title.label}
            </Typography>
            <Typography
              sx={{
                ...theme.nawy.typography.english.variants.body.md.regular,
                color: theme.palette.text.primary,
              }}
            >
              {formatValue(title)}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Section 3: Sales Link Button */}
      <Box>
        <NwButton
          variant="text"
          onClick={() => {
            window.open(
              `${EnvironmentVariables.SALES_PORTAL_URL}/${
                data.oldId || data.id
              }`,
              "_blank"
            );
          }}
          sx={{
            padding: 0,
          }}
        >
          Sale Link
        </NwButton>
      </Box>
    </Box>
  );
};
